if (!customElements.get('nak-slide-show')) {
  class NakSlideShow extends HTMLElement {
    constructor() {
      super();
      if (window.initLazyScript) {
        window.initLazyScript(this, this.init.bind(this));
      } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.init.bind(this));
      } else {
        this.init();
      }
    }

    init() {
      this.track = this.querySelector('.nak-ss__track');
      this.slides = this.querySelectorAll('.nak-ss__slide');
      this.dotsContainer = this.querySelector('.nak-ss__dots');
      this.navEl = this.querySelector('.nak-ss__nav');
      this.counterEl = this.querySelector('.nak-ss__counter-current');
      this.currentIndex = 0;
      this.swipeThreshold = 60;
      this.swipeLocked = false;
      this.rtl = document.dir === 'rtl';

      if (this.dotsContainer) {
        this.dots = this.querySelectorAll('.nak-ss__dot');
        this.dotsContainer.addEventListener('click', this.handleDotClick.bind(this));
      }

      if (this.navEl) {
        this.navEl.addEventListener('click', this.handleNavClick.bind(this));
      }

      if ('ontouchstart' in window) {
        this.track.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
        this.track.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
      }

      this.setSlideVisibility();

      if (this.dataset.autoplay === 'true') {
        this.initAutoplay();
      }
    }

    handleNavClick(e) {
      const btn = e.target.closest('.nak-ss__nav-btn');
      if (!btn) return;
      if (btn.dataset.dir === 'prev') this.showPrevSlide();
      if (btn.dataset.dir === 'next') this.showNextSlide();
    }

    handleDotClick(e) {
      const dot = e.target.closest('.nak-ss__dot');
      if (!dot) return;
      this.autoplayTimeLeft = this.autoplaySpeed;
      this.setActiveSlide(parseInt(dot.dataset.index));
    }

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
      this.touchStartY = e.changedTouches[0].screenY;
    }

    handleTouchEnd(e) {
      if (this.swipeLocked) return;
      const dx = e.changedTouches[0].screenX - this.touchStartX;
      const dy = e.changedTouches[0].screenY - this.touchStartY;
      if (Math.abs(dx) < this.swipeThreshold || Math.abs(dy) > Math.abs(dx)) return;
      this.swipeLocked = true;
      setTimeout(() => { this.swipeLocked = false; }, 600);
      if (dx < 0) {
        this.rtl ? this.showPrevSlide() : this.showNextSlide();
      } else {
        this.rtl ? this.showNextSlide() : this.showPrevSlide();
      }
    }

    showNextSlide() {
      let next = this.currentIndex + 1;
      if (next >= this.slides.length) next = 0;
      this.setActiveSlide(next, 'next');
    }

    showPrevSlide() {
      let prev = this.currentIndex - 1;
      if (prev < 0) prev = this.slides.length - 1;
      this.setActiveSlide(prev, 'prev');
    }

    setActiveSlide(index, dir) {
      if (index === this.currentIndex) return;
      const prevSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[index];

      prevSlide.classList.add('transition-out');

      setTimeout(() => {
        this.track.scrollTo({ left: nextSlide.offsetLeft, behavior: 'instant' });
        nextSlide.classList.add('is-active');
        prevSlide.classList.remove('is-active', 'transition-out');

        if (this.autoplayEnabled) this.autoplayStartTime = Date.now();
        if (this.counterEl) this.counterEl.textContent = index + 1;

        this.currentIndex = index;
        this.setSlideVisibility();
        this.updateDots();
      }, 200);
    }

    updateDots() {
      if (!this.dots) return;
      this.dots.forEach((dot, i) => {
        if (i === this.currentIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    setSlideVisibility() {
      this.slides.forEach((slide, i) => {
        const links = slide.querySelectorAll('a');
        if (i === this.currentIndex) {
          slide.setAttribute('aria-hidden', 'false');
          slide.removeAttribute('tabindex');
          links.forEach(a => a.removeAttribute('tabindex'));
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('tabindex', '-1');
          links.forEach(a => a.setAttribute('tabindex', '-1'));
        }
      });
    }

    /* ---- Autoplay ---- */
    initAutoplay() {
      this.autoplayEnabled = true;
      this.autoplaySpeed = parseInt(this.dataset.speed || '6') * 1000;
      this.addEventListener('mouseenter', this.pauseAutoplay.bind(this));
      this.addEventListener('mouseleave', this.resumeAutoplay.bind(this));
      this.track.addEventListener('focusin', this.pauseAutoplay.bind(this));
      this.track.addEventListener('focusout', this.resumeAutoplay.bind(this));
      this.addVisibilityObserver();
      this.startAutoplay();
    }

    startAutoplay() {
      this.autoplayPaused = false;
      this.autoplayStartTime = Date.now();
      this.autoplayTimer = setInterval(this.showNextSlide.bind(this), this.autoplaySpeed);
    }

    stopAutoplay() {
      this.autoplayPaused = true;
      clearInterval(this.autoplayTimer);
      clearTimeout(this.resumeTimer);
    }

    pauseAutoplay() {
      if (!this.autoplayEnabled || this.autoplayPaused) return;
      this.stopAutoplay();
      const elapsed = Date.now() - (this.resumedTime || this.autoplayStartTime);
      this.autoplayTimeLeft = this.autoplayTimeLeft
        ? this.autoplayTimeLeft - elapsed
        : this.autoplaySpeed - elapsed;
      if (this.autoplayTimeLeft < 0) this.autoplayTimeLeft = 0;
    }

    resumeAutoplay() {
      if (!this.autoplayEnabled || !this.autoplayPaused) return;
      this.resumedTime = Date.now();
      this.resumeTimer = setTimeout(() => {
        this.showNextSlide();
        this.startAutoplay();
      }, Math.max(0, this.autoplayTimeLeft || 0));
      this.autoplayPaused = false;
    }

    addVisibilityObserver() {
      if (!('IntersectionObserver' in window)) return;
      new IntersectionObserver(entries => {
        entries.forEach(entry => {
          entry.isIntersecting ? this.resumeAutoplay() : this.pauseAutoplay();
        });
      }).observe(this);
    }
  }

  customElements.define('nak-slide-show', NakSlideShow);
}
